// Pure nav policy: which sidebar sections a session sees.
//
// Two deployment kinds share one SPA image:
//  - instances (Getty production, Navcore, the playground) — the full product;
//  - the hub (hub.trackizy.com) — discovery + fleet monitoring only. It has no
//    orgs, no devices and no Traccar, so the org-facing pages and the sultan
//    sections that act on *local* orgs are pure foot-guns there.
//
// On the hub, an Instance Admin sees only HUB_ALLOWED_NAV. This is accident
// prevention, not security: the APIs stay reachable by design (see the
// central-hub direction note, decision 5). Everywhere else, behavior is
// unchanged: the two hub-only items are hidden because the hub APIs 403
// off-hub anyway.
//
// Platform items carry an optional `cap`: `'admin'` = Instance Admin only, a
// capability key = support needs it, absent = every instance staff member.

/** Shown only on the hub deployment (the API 403s elsewhere). */
export const HUB_ONLY_NAV = ['/sultan/servers', '/sultan/backups'];

/** The ONLY sultan items shown on the hub: the fleet registry + backups view,
 *  Integrations (fleet-alert recipients live there), and Branding (the hub's
 *  own login page). Everything else acts on local orgs the hub doesn't have. */
export const HUB_ALLOWED_NAV = [
  '/sultan/servers',
  '/sultan/backups',
  '/sultan/integrations',
  '/sultan/branding',
];

const keepItems = (sections, keep) =>
  sections
    .map((s) => ({ ...s, items: s.items.filter((it) => keep(it)) }))
    .filter((s) => s.items.length > 0);

/** Whether `access` may see a platform item, given its optional `cap`. */
export function itemAllowed(item, access) {
  if (!item.cap) return true;
  if (access.isAdmin) return true;
  if (item.cap === 'admin') return false;
  return access.capabilities.includes(item.cap);
}

export function resolveActiveSections({
  isHub,
  access,
  sidebarMode,
  hasReseller,
  hasOrg,
  navSections,
  platformSections,
  resellerSections,
  mode = 'normal',
  enterpriseSections = [],
}) {
  const a = access || { isAdmin: false, isStaff: false, capabilities: [] };
  // Enterprise mode is its own read-only product surface: the synthetic org
  // must never unlock the fleet nav.
  if (mode === 'enterprise') return enterpriseSections;
  if (isHub && a.isAdmin) {
    // Order by the allow-list, not the source order, so the sidebar leads
    // with Servers — the page that is this box's actual job.
    const byId = new Map(
      platformSections.flatMap((s) => s.items.map((it) => [it.id, { section: s, item: it }])),
    );
    const items = HUB_ALLOWED_NAV.map((id) => byId.get(id)?.item).filter(Boolean);
    const template = platformSections[0];
    return items.length > 0 ? [{ ...template, items }] : [];
  }

  // `resellerSections` is the Enterprise entry: alone for a reseller member
  // with no org, appended to the fleet nav for one with an org of their own.
  // While managing a company (impersonated) the banner is the way back.
  const baseSections = a.isStaff && sidebarMode === 'platform'
    ? keepItems(platformSections, (it) => itemAllowed(it, a))
    : hasReseller && !a.isStaff && !hasOrg
      ? resellerSections
      : hasReseller && mode === 'normal'
        ? [...navSections, ...resellerSections]
        : navSections;

  if (isHub) return baseSections;
  return keepItems(baseSections, (it) => !HUB_ONLY_NAV.includes(it.id));
}

/**
 * The single nav item a pathname belongs to: the LONGEST item id that equals
 * the path or is a whole-segment prefix of it. Plain `startsWith` lit up
 * `/sultan` (Orgs) on every `/sultan/*` page and `/work` on `/work-templates`,
 * and titled every Instance Admin page "Orgs". `/` matches only itself.
 * Drives both the sidebar highlight and the top-bar title.
 */
export function activeNavItem(items, pathname) {
  let best = null;
  for (const item of items) {
    const id = item.id;
    const hit = id === '/' ? pathname === '/' : pathname === id || pathname.startsWith(`${id}/`);
    if (hit && (!best || id.length > best.id.length)) best = item;
  }
  return best;
}
